import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./variables.css";
import "./TeamSchedule.css";
import API_URL from "./config";
import useTeamTheme from "./useTeamTheme";
import { getTeamTheme } from "./teamTheme";

const TeamSchedule = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [games, setGames] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useTeamTheme(team);

  useEffect(() => {
    const fetchSchedule = async () => {
      setLoading(true);
      try {
        // Get active save from localStorage
        const activeSaveId = localStorage.getItem("activeSaveId");
        
        // Build URL with save_id parameter if available
        let url = `${API_URL}/api/teams/${teamId}/schedule`;
        if (activeSaveId) {
          url += `?save_id=${activeSaveId}`;
        }
        
        const res = await fetch(url);
        if (!res.ok) {
          const data = await res.json();
          setError(data.detail || "Failed to load schedule");
          return;
        }
        const data = await res.json();
        setTeam(data.team || null);
        setGames(data.games || []);
      } catch (err) {
        setError(err.message || "Server error");
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, [teamId]);

  const formatDate = (iso) => {
    if (!iso) return "TBD";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch (e) {
      return iso;
    }
  };

  const formatDayOfWeek = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-US", { weekday: "short" });
    } catch (e) {
      return "";
    }
  };

  const getGameStatus = (game) => {
    if (!game.played) return 'upcoming';

    // Determine if user's team won or lost
    const isHomeTeam = game.homeTeam === teamId;
    const userScore = isHomeTeam ? game.homeScore : game.awayScore;
    const opponentScore = isHomeTeam ? game.awayScore : game.homeScore;

    return userScore > opponentScore ? 'won' : 'lost';
  };

  const getTeamIconStyle = (teamMeta) => {
    const { primary, secondary } = getTeamTheme(teamMeta);
    return {
      background: `linear-gradient(135deg, ${primary}, ${secondary})`,
      border: `2px solid ${secondary}`,
      boxShadow: `0 0 0 1px ${primary} inset`,
    };
  };

  return (
    <>
      <Navbar />
      <div className="schedule-container">
        {/* Header */}
        <header className="schedule-header">
          <div className="header-content">
            {team && (
              <div className="team-header-info">
                <div className="team-logo-small">
                  <span className="team-initial-small">{team.name?.charAt(0)}</span>
                </div>
                <div>
                  <h1 className="schedule-title">{team.name} Schedule</h1>
                  {team.city && <p className="schedule-subtitle">{team.city}</p>}
                </div>
              </div>
            )}
          </div>
        </header>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠</span>
          <p>{error}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="schedule-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading schedule...</p>
          </div>
        ) : (
          <>
            {games.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📅</div>
                <h2>No Games Scheduled</h2>
                <p>This team doesn't have any games on their schedule yet</p>
              </div>
            ) : (
              <div className="games-container">
                <div className="games-header">
                  <h2 className="games-title">Season Schedule</h2>
                  <span className="games-count">{games.length} Games</span>
                </div>

                <div className="games-grid">
                  {games.map((game) => (
                    <div
                      key={game._id}
                      className={`game-card ${getGameStatus(game)}${game.played && game.resultId ? ' clickable' : ''}`}
                      onClick={() => game.played && game.resultId && navigate(`/game-detail/${game.resultId}`)}
                    >
                      <div className="game-card-header">
                        <div className="game-date">
                          <span className="date-day">{formatDayOfWeek(game.date)}</span>
                          <span className="date-full">{formatDate(game.date)}</span>
                        </div>
                        <div className={`game-status ${game.played ? 'final' : 'scheduled'}`}>
                          {game.played ? 'Final' : 'Scheduled'}
                        </div>
                      </div>

                      <div className="game-matchup">
                        <div className="game-team away-team">
                          <div className="team-info">
                            <div className="team-icon" style={getTeamIconStyle(game.awayTeamName)}>
                              <span className="team-icon-text">{game.awayTeamName?.charAt(0) || 'A'}</span>
                            </div>
                            <div className="team-details">
                              <span className="team-label">Away</span>
                              <span className="team-name">{game.awayTeamName || 'TBD'}</span>
                            </div>
                          </div>
                          {game.played && (
                            <div className="team-score">
                              <span className="score-value">{game.awayScore ?? '-'}</span>
                            </div>
                          )}
                        </div>

                        <div className="matchup-divider">
                          <span className="vs-text">vs</span>
                        </div>

                        <div className="game-team home-team">
                          <div className="team-info">
                            <div className="team-icon" style={getTeamIconStyle(game.homeTeamName)}>
                              <span className="team-icon-text">{game.homeTeamName?.charAt(0) || 'H'}</span>
                            </div>
                            <div className="team-details">
                              <span className="team-label">Home</span>
                              <span className="team-name">{game.homeTeamName || 'TBD'}</span>
                            </div>
                          </div>
                          {game.played && (
                            <div className="team-score">
                              <span className="score-value">{game.homeScore ?? '-'}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="game-card-footer">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {game.played && (
                            <span
                              className={`result-badge ${getGameStatus(game)}`}
                              style={{
                                fontSize: '1rem',
                                fontWeight: 800,
                                padding: '0.375rem 0.625rem',
                                borderRadius: '6px',
                                backgroundColor: getGameStatus(game) === 'won' ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                color: getGameStatus(game) === 'won' ? '#4ade80' : 'var(--color-text-error)'
                              }}
                            >
                              {getGameStatus(game) === 'won' ? 'W' : 'L'}
                            </span>
                          )}
                          {game.homeTeam === teamId ? (
                            <span className="location-badge home">🏠 Home Game</span>
                          ) : (
                            <span className="location-badge away">✈️ Away Game</span>
                          )}
                          {!game.played && (
                            <button
                              onClick={() => navigate("/play-game", { state: { game } })}
                              style={{
                                padding: "8px 16px",
                                background: "var(--color-primary)",
                                color: "white",
                                border: "none",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight: 600,
                                width: "fit-content"
                              }}
                            >
                              ▶ Play Game
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default TeamSchedule;
